import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, Loader2, Check, Clock, XCircle, Lock, RefreshCw } from "lucide-react";
import useGoerOneAuth from "../../../hooks/useGoerOneAuth";
import { formatINR } from "../VoucherCheckoutPanel";
import { fetchInviteStatus } from "../../../store/loyaltySlices/membershipInviteSlice";

/**
 * "I have an invitation": checks the customer's request for an invite-only plan.
 *   approved -> continue to the normal pay / join flow
 *   pending  -> under review
 *   rejected -> shows the reason, can request again
 *   none     -> no request yet, can request consideration
 *
 * open / program / onClose
 * onContinue(program): approved customer continues (MembershipPage runs its normal select flow)
 * onRequest(program):  opens the request-consideration form
 */
export default function InvitationStatusModal({ open, program, onClose, onContinue, onRequest }) {
    const dispatch = useDispatch();
    const { user } = useGoerOneAuth();
    const statusByProgram = useSelector((state) => state.membershipInvite.statusByProgram);

    const [checked, setChecked] = useState(false); // this open's GET has finished
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    // Always hit the API on open (cached status in the store could be stale)
    useEffect(() => {
        if (!open || !program) return;
        setChecked(false);
        setFailed(false);
        if (!user?.id) {
            setChecked(true);
            return;
        }
        let cancelled = false;
        dispatch(fetchInviteStatus(program.program_id)).then((action) => {
            if (cancelled) return;
            if (fetchInviteStatus.rejected.match(action)) setFailed(true);
            setChecked(true);
        });
        return () => {
            cancelled = true;
        };
    }, [open, program?.program_id, user?.id, attempt, dispatch]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open || !program) return null;

    const info = statusByProgram[program.program_id];
    const state = info?.invite_status; // 0 pending, 1 approved, 2 rejected, null/undefined none
    const isPaid = program.membership_type === "paid";
    const amount = Number(program.membership_amount) || 0;

    const continueLabel = isPaid ? `Continue to pay ${formatINR(amount)}` : "Join free";

    const handleContinue = () => {
        onClose?.();
        onContinue?.(program);
    };

    const handleRequest = () => {
        onClose?.();
        onRequest?.(program);
    };

    const Panel = ({ icon, tone, title, text, children }) => (
        <div className="px-5 py-10 text-center">
            <span className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${tone}`}>{icon}</span>
            <p className="mt-3 text-base font-bold text-gray-900">{title}</p>
            <p className="mx-auto mt-1 max-w-xs text-sm text-gray-500">{text}</p>
            <div className="mt-6 flex flex-col items-center gap-2">{children}</div>
        </div>
    );

    const primary =
        "inline-flex items-center justify-center gap-2 rounded-full bg-[#0B67FC] px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:brightness-95 active:scale-[0.98]";
    const secondary = "rounded-full px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100";

    let body;
    if (!user?.id) {
        body = (
            <Panel
                icon={<Lock size={22} />}
                tone="bg-gray-100 text-gray-600"
                title="Log in to continue"
                text="Log in to check the status of your invitation."
            >
                <button type="button" onClick={onClose} className={secondary}>
                    Close
                </button>
            </Panel>
        );
    } else if (!checked) {
        body = (
            <div className="flex items-center justify-center gap-2 px-5 py-14 text-sm text-gray-500">
                <Loader2 size={18} className="animate-spin" />
                Checking your invitation
            </div>
        );
    } else if (failed) {
        body = (
            <Panel
                icon={<RefreshCw size={22} />}
                tone="bg-gray-100 text-gray-600"
                title="Couldn't check your invitation"
                text="Something went wrong on our side. Try again in a moment."
            >
                <button type="button" onClick={() => setAttempt((n) => n + 1)} className={primary}>
                    Try again
                </button>
                <button type="button" onClick={onClose} className={secondary}>
                    Close
                </button>
            </Panel>
        );
    } else if (state === 1) {
        body = (
            <Panel
                icon={<Check size={24} strokeWidth={3} />}
                tone="bg-emerald-100 text-emerald-600"
                title="Your invitation is approved"
                text={`You can now ${isPaid ? "pay for" : "join"} ${program.program_name}.`}
            >
                <button type="button" onClick={handleContinue} className={primary}>
                    {continueLabel}
                </button>
            </Panel>
        );
    } else if (state === 0) {
        body = (
            <Panel
                icon={<Clock size={24} />}
                tone="bg-amber-100 text-amber-600"
                title="Still under review"
                text="We haven't decided on your request yet. You'll be able to pay for this membership as soon as it's approved."
            >
                <button type="button" onClick={() => setAttempt((n) => n + 1)} className={primary}>
                    Check again
                </button>
                <button type="button" onClick={onClose} className={secondary}>
                    Close
                </button>
            </Panel>
        );
    } else if (state === 2) {
        body = (
            <Panel
                icon={<XCircle size={24} />}
                tone="bg-red-100 text-red-600"
                title="Your request wasn't approved"
                text={info?.rejection_reason || "Your last request for this membership was declined."}
            >
                <button type="button" onClick={handleRequest} className={primary}>
                    Request again
                </button>
                <button type="button" onClick={onClose} className={secondary}>
                    Close
                </button>
            </Panel>
        );
    } else {
        body = (
            <Panel
                icon={<Lock size={22} />}
                tone="bg-gray-100 text-gray-600"
                title="No invitation found"
                text={`We couldn't find a request for ${program.program_name}. Ask to be considered and we'll review it.`}
            >
                <button type="button" onClick={handleRequest} className={primary}>
                    Request consideration
                </button>
                <button type="button" onClick={onClose} className={secondary}>
                    Close
                </button>
            </Panel>
        );
    }

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Invitation for ${program.program_name}`}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
            onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
        >
            <div className="w-full max-w-md overflow-hidden rounded-t-3xl bg-white shadow-xl sm:rounded-3xl">
                <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
                    <div className="min-w-0">
                        <h2 className="text-lg font-extrabold text-gray-900">Your invitation</h2>
                        <p className="truncate text-sm text-gray-500">{program.program_name} is invite only</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="-mr-1 rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                    >
                        <X size={20} />
                    </button>
                </div>
                {body}
            </div>
        </div>
    );
}